import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle, Loader2, XCircle, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading"); // loading, success, failed, expired
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("failed");
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempt = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempt >= maxAttempts) {
      setStatus("failed");
      return;
    }

    setPollCount(attempt + 1);

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        setPaymentData(data);
        
        // Update local storage with new plan info
        const issuerData = JSON.parse(localStorage.getItem('issuer_data') || '{}');
        issuerData.is_paid = true;
        issuerData.plan_name = data.plan_name;
        localStorage.setItem('issuer_data', JSON.stringify(issuerData));
        
        return;
      } else if (data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempt + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      if (attempt < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempt + 1), pollInterval);
      } else {
        setStatus("failed");
      }
    }
  };

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-6">
              <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>
              Processing Payment...
            </h1>
            <p className="text-slate-600 mb-2">Please wait while we confirm your payment</p>
            <p className="text-sm text-slate-400">Checking status ({pollCount}/10)</p>
          </div>
        );

      case "success":
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>
              Payment Successful!
            </h1>
            <p className="text-slate-600 mb-6">
              Welcome to the <span className="font-semibold text-blue-600">{paymentData?.plan_name}</span> plan!
            </p>
            
            <div className="bg-slate-50 rounded-xl p-6 mb-8 max-w-md mx-auto">
              <h3 className="font-semibold text-slate-900 mb-3">What's next?</h3>
              <ul className="text-left space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  Your account has been upgraded
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  Start creating certificates immediately
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  Receipt sent to your email
                </li>
              </ul>
            </div>

            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => navigate("/issuer/dashboard")}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-8 py-3"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
              <Button
                onClick={() => navigate("/issuer/create")}
                variant="outline"
                className="border-slate-200 rounded-lg px-8 py-3"
                data-testid="create-certificate-btn"
              >
                Create Certificate
              </Button>
            </div>
          </div>
        );

      case "expired":
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-amber-100 rounded-full mb-6">
              <RefreshCw className="h-10 w-10 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>
              Session Expired
            </h1>
            <p className="text-slate-600 mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Button
              onClick={() => navigate("/pricing")}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-8 py-3"
              data-testid="try-again-btn"
            >
              Return to Pricing
            </Button>
          </div>
        );

      case "failed":
      default:
        return (
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
              <XCircle className="h-10 w-10 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display'}}>
              Payment Issue
            </h1>
            <p className="text-slate-600 mb-6">
              We couldn't verify your payment. If you were charged, please contact support.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                onClick={() => navigate("/pricing")}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-8 py-3"
                data-testid="return-pricing-btn"
              >
                Return to Pricing
              </Button>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="border-slate-200 rounded-lg px-8 py-3"
                data-testid="retry-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-6">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl p-8" data-testid="payment-result-card">
        {renderContent()}
      </div>
    </div>
  );
};

export default PaymentSuccess;
